'use client';

import type { ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import type { BestBuyPickupStore } from './api';
import { formatPickupDate } from './pickupDate';
import styles from './BuyPagePickup.module.css';

const formatDistance = (miles: number): string =>
  miles < 10 ? miles.toFixed(1) : String(Math.round(miles));

const isLimited = (store: BestBuyPickupStore, sku: string | null): boolean =>
  store.hasLimitedQuantity ||
  Boolean(store.items.find((item) => item.sku === sku)?.limitedQuantityText);

/** One store's name, distance, stock and pickup date. Shared by the inline
 * radio rows and the "Show all stores" dialog, so the two never drift. */
export const StoreRowContent = ({
  store,
  sku,
  afterName,
}: {
  store: BestBuyPickupStore;
  sku: string | null;
  /** Inline right after the store name (the dialog's selected tick). */
  afterName?: ReactNode;
}) => {
  const t = useTranslations('ring-pro');
  const locale = useLocale();
  const selectable = store.allItemsAvailable;
  const date = store.pickup_date
    ? formatPickupDate(store.pickup_date, locale)
    : null;

  return (
    <>
      <span className={styles.storeMain}>
        <span className={styles.storeName}>
          {t('ring-pro.bestBuyPickup.storeName', { name: store.name })}
          {afterName}
        </span>
        <span className={styles.storeMeta}>
          {store.distance_in_miles !== undefined ? (
            <>
              <span className={styles.muted}>
                {t('ring-pro.bestBuyPickup.distanceMi', {
                  distance: formatDistance(store.distance_in_miles),
                })}
              </span>
              <span className={styles.muted} aria-hidden="true">
                ·
              </span>
              <span className={styles.visuallyHidden}>, </span>
            </>
          ) : null}
          {selectable ? (
            isLimited(store, sku) ? (
              <span className={styles.fewLeft} data-testid="bb-stock-few">
                {t('ring-pro.bestBuyPickup.fewLeft')}
              </span>
            ) : (
              <span className={styles.inStock} data-testid="bb-stock-in">
                {t('ring-pro.bestBuyPickup.inStock')}
              </span>
            )
          ) : (
            <span className={styles.muted}>
              {store.availabilityText ||
                t('ring-pro.bestBuyPickup.storeUnavailable')}
            </span>
          )}
        </span>
      </span>
      {selectable && (date || store.title) ? (
        <span className={styles.storeWhen}>
          {date ? (
            <>
              <span className={styles.whenLabel}>
                {t('ring-pro.bestBuyPickup.pickupColumnLabel')}
              </span>
              <span className={styles.whenDate} data-testid="bb-store-date">
                {date}
              </span>
            </>
          ) : (
            // No usable date: the server's own promise line.
            <span className={styles.whenDate}>{store.title}</span>
          )}
        </span>
      ) : null}
    </>
  );
};
